import { Component } from '@theme/component';

/**
 * IT collection — the copy block's Read more.
 *
 * Retires its own toggle when the copy already fits: a control that does
 * nothing is worse than no control. Short collection descriptions are
 * common, so this is the usual case rather than an edge one.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} body - The clamped copy.
 * @property {HTMLButtonElement} toggle - The Read more / Read less button.
 * @extends Component<Refs>
 */
class ItCollectionContent extends Component {
  requiredRefs = ['body', 'toggle'];

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  connectedCallback() {
    super.connectedCallback();

    this.#sync();
    this.#resizeObserver = new ResizeObserver(() => this.#sync());
    this.#resizeObserver.observe(this.refs.body);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver?.disconnect();
  }

  /**
   * Opens or closes the copy. Wired from the button's `on:click="/toggle"`.
   */
  toggle() {
    const { body, toggle } = this.refs;
    const open = body.classList.toggle('is-open');

    toggle.textContent = open ? toggle.dataset.less ?? '' : toggle.dataset.more ?? '';
    toggle.setAttribute('aria-expanded', String(open));
  }

  #sync() {
    const { body, toggle } = this.refs;
    if (body.classList.contains('is-open')) return;

    toggle.hidden = body.scrollHeight <= body.clientHeight + 2;
  }
}

if (!customElements.get('it-collection-content')) {
  customElements.define('it-collection-content', ItCollectionContent);
}
